import { Box, Stack, Typography } from '@mui/material';
import {
  EscalationResponseType,
  EscalationRule,
  EscalationTimeConditionType,
  formatWithinBusinessDays,
  SERVICE_LEVEL_PRIORITY_ORDER,
} from '@provision/sidekick-utils';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormAutocomplete } from 'src/components/Forms/FormAutocomplete/form-autocomplete';
import { FormSelect } from 'src/components/Forms/FormSelect/form-select';
import { FormTextField } from 'src/components/Forms/FormTextField/form-text-field';
import { FormToggleGroup } from 'src/components/Forms/FormToggleGroup/form-toggle-group';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalStep, useModalStep } from 'src/components/ModalContainer';
import {
  ESCALATION_RESPONSE_TYPE_OPTIONS,
  ESCALATION_TIME_CONDITION_OPTIONS,
  ESCALATION_URGENCY_DISPLAY_LABELS,
  ESCALATION_WEEKDAY_OPTIONS,
  formatEnumLabel,
} from './escalation-utils';

interface EscalationRuleStepProps {
  initialRule?: EscalationRule | null;
  order: number;
  urgencyLevel: string;
}

enum EscalationRuleField {
  Name = 'name',
  ServiceLevelSelector = 'serviceLevelSelector',
  ServiceLevels = 'serviceLevels',
  TimeCondition = 'timeCondition',
  ResponseType = 'responseType',
  DurationMinutes = 'durationMinutes',
  BusinessDays = 'businessDays',
  FixedTime = 'fixedTime',
  WeekDay = 'weekDay',
  CustomWeekDays = 'customWeekDays',
  CustomTimeFrom = 'customTimeFrom',
  CustomTimeTo = 'customTimeTo',
}

enum ServiceLevelSelector {
  All = 'all',
  Selected = 'selected',
}

interface EscalationRuleFormValues {
  [EscalationRuleField.Name]: string;
  [EscalationRuleField.ServiceLevelSelector]: ServiceLevelSelector;
  [EscalationRuleField.ServiceLevels]: string[];
  [EscalationRuleField.TimeCondition]: EscalationTimeConditionType;
  [EscalationRuleField.ResponseType]: EscalationResponseType;
  [EscalationRuleField.DurationMinutes]: string;
  [EscalationRuleField.BusinessDays]: string;
  [EscalationRuleField.FixedTime]: string;
  [EscalationRuleField.WeekDay]: string;
  [EscalationRuleField.CustomWeekDays]: string[];
  [EscalationRuleField.CustomTimeFrom]: string;
  [EscalationRuleField.CustomTimeTo]: string;
}

const TIME_ESCALATION_RESPONSE_TYPES: EscalationResponseType[] = [
  'by_fixed_time',
  'by_next_business_day',
  'by_tomorrow',
];

const MAX_BUSINESS_DAYS = 10;
const DEFAULT_CUSTOM_WEEK_DAYS = [1, 2, 3, 4, 5];
const NO_WEEKDAY = '';

const SERVICE_LEVEL_SELECTOR_OPTIONS = [
  { id: ServiceLevelSelector.All, value: ServiceLevelSelector.All, label: 'All' },
  {
    id: ServiceLevelSelector.Selected,
    value: ServiceLevelSelector.Selected,
    label: 'Selected service levels',
  },
];

const TIME_CONDITION_OPTIONS = ESCALATION_TIME_CONDITION_OPTIONS.map(({ value, label }) => ({
  id: value,
  value,
  label,
}));

const RESPONSE_TYPE_OPTIONS = ESCALATION_RESPONSE_TYPE_OPTIONS.map(({ value, label }) => ({
  id: value,
  value,
  label,
}));

const WEEKDAY_SELECT_OPTIONS = [
  { id: NO_WEEKDAY, value: NO_WEEKDAY, label: 'None' },
  ...ESCALATION_WEEKDAY_OPTIONS.map(({ value, label }) => ({
    id: String(value),
    value: String(value),
    label,
  })),
];

const CUSTOM_DAY_OPTIONS = ESCALATION_WEEKDAY_OPTIONS.map(({ value, label }) => ({
  id: String(value),
  value: String(value),
  label: label.slice(0, 3),
  testId: `escalation-rule-custom-day-${value}`,
}));

const previewSx = {
  backgroundColor: 'action.hover',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  p: 1.5,
};

/** A step of the policy modal; it pops with the rule it built, or with nothing when dismissed. */
export const EscalationRuleStep = ({
  initialRule,
  order,
  urgencyLevel,
}: EscalationRuleStepProps) => {
  const { popStep } = useModalStep<EscalationRule>();
  const defaultValues = useMemo(() => buildDefaultValues(initialRule), [initialRule]);
  const formMethods = useForm<EscalationRuleFormValues>({
    defaultValues,
    mode: 'onChange',
    shouldUnregister: true,
  });
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = formMethods;

  const [
    serviceLevelSelector,
    serviceLevels,
    timeCondition,
    responseType,
    durationMinutes,
    businessDays,
    fixedTime,
    weekDay,
  ] = useWatch({
    control,
    name: [
      EscalationRuleField.ServiceLevelSelector,
      EscalationRuleField.ServiceLevels,
      EscalationRuleField.TimeCondition,
      EscalationRuleField.ResponseType,
      EscalationRuleField.DurationMinutes,
      EscalationRuleField.BusinessDays,
      EscalationRuleField.FixedTime,
      EscalationRuleField.WeekDay,
    ],
  });

  const rulePreview = buildRulePreview({
    urgencyLabel: ESCALATION_URGENCY_DISPLAY_LABELS[urgencyLevel] ?? urgencyLevel,
    allServiceLevels: serviceLevelSelector === ServiceLevelSelector.All,
    serviceLevels: serviceLevels ?? defaultValues.serviceLevels,
    timeCondition: timeCondition ?? defaultValues.timeCondition,
    responseType: responseType ?? defaultValues.responseType,
    durationMinutes: Number(durationMinutes ?? defaultValues.durationMinutes),
    businessDays: Number(businessDays ?? defaultValues.businessDays),
    fixedTime: fixedTime ?? defaultValues.fixedTime,
    weekDay: toWeekDay(weekDay ?? defaultValues.weekDay),
  });

  const onSubmit = (values: EscalationRuleFormValues) => {
    popStep(toEscalationRule(values, initialRule?.id ?? crypto.randomUUID(), order));
  };

  const currentResponseType = responseType ?? defaultValues.responseType;

  return (
    <ModalStep
      title={initialRule ? 'Edit Rule' : 'Add Rule'}
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'escalation-rule-cancel' },
        {
          label: initialRule ? 'Save rule' : 'Add rule',
          kind: 'primary',
          onClick: handleSubmit(onSubmit),
          disabled: !isValid,
          testId: 'escalation-rule-save',
        },
      ]}
    >
      <GenericForm<EscalationRuleFormValues>
        formMethods={formMethods}
        data-testid="escalation-rule-modal"
      >
        <Box sx={previewSx} data-testid="escalation-rule-preview">
          <Typography variant="overline" color="text.secondary">
            Rule Preview
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {rulePreview}
          </Typography>
        </Box>

        <FormTextField
          type="textfield"
          data-testid="escalation-rule-name"
          name={EscalationRuleField.Name}
          label="Rule name"
          textFieldProps={{ placeholder: 'e.g., Signature clients after hours' }}
        />

        <FormSelect
          type="select"
          data-testid="escalation-rule-service-level-selector"
          name={EscalationRuleField.ServiceLevelSelector}
          label="Service level selector"
          options={SERVICE_LEVEL_SELECTOR_OPTIONS}
        />

        {serviceLevelSelector === ServiceLevelSelector.Selected ? (
          <FormAutocomplete
            type="autocomplete"
            data-testid="escalation-rule-service-levels"
            name={EscalationRuleField.ServiceLevels}
            label="Service levels"
            multiple
            required="At least one service level is required"
            options={[...SERVICE_LEVEL_PRIORITY_ORDER]}
          />
        ) : null}

        <FormSelect
          type="select"
          data-testid="escalation-rule-time-condition"
          name={EscalationRuleField.TimeCondition}
          label="Escalates when"
          options={TIME_CONDITION_OPTIONS}
        />

        {timeCondition === 'custom' ? (
          <>
            <FormToggleGroup
              type="toggle-group"
              multiple
              data-testid="escalation-rule-days"
              name={EscalationRuleField.CustomWeekDays}
              label="Days"
              required="At least one day must be selected"
              options={CUSTOM_DAY_OPTIONS}
            />
            <Stack direction="row" gap={1.5}>
              <FormTextField
                type="textfield"
                data-testid="escalation-rule-time-from"
                name={EscalationRuleField.CustomTimeFrom}
                label="From"
                textFieldProps={{ type: 'time' }}
                sx={{ flex: 1 }}
              />
              <FormTextField
                type="textfield"
                data-testid="escalation-rule-time-to"
                name={EscalationRuleField.CustomTimeTo}
                label="To"
                textFieldProps={{ type: 'time' }}
                sx={{ flex: 1 }}
              />
            </Stack>
          </>
        ) : null}

        <FormSelect
          type="select"
          data-testid="escalation-rule-response-type"
          name={EscalationRuleField.ResponseType}
          label="Response"
          options={RESPONSE_TYPE_OPTIONS}
        />

        {currentResponseType === 'fixed_duration' ? (
          <FormTextField
            type="textfield"
            data-testid="escalation-rule-duration"
            name={EscalationRuleField.DurationMinutes}
            label="Duration (minutes)"
            textFieldProps={{ type: 'number' }}
            showErrorText
            rules={{
              validate: (value: string) => Number(value) > 0 || 'Must be greater than 0',
            }}
          />
        ) : null}

        {currentResponseType === 'within_business_days' ? (
          <FormTextField
            type="textfield"
            data-testid="escalation-rule-business-days"
            name={EscalationRuleField.BusinessDays}
            label="Business days"
            textFieldProps={{
              type: 'number',
              inputProps: { min: 1, max: MAX_BUSINESS_DAYS, step: 1 },
            }}
            showErrorText
            description="Due at close of business; the day it's escalated doesn't count"
            rules={{
              validate: (value: string) => {
                const days = Number(value);
                return (
                  (Number.isInteger(days) && days >= 1 && days <= MAX_BUSINESS_DAYS) ||
                  `Must be a whole number from 1 to ${MAX_BUSINESS_DAYS}`
                );
              },
            }}
          />
        ) : null}

        {TIME_ESCALATION_RESPONSE_TYPES.includes(currentResponseType) ? (
          <FormTextField
            type="textfield"
            data-testid="escalation-rule-time"
            name={EscalationRuleField.FixedTime}
            label="Time"
            textFieldProps={{ type: 'time' }}
          />
        ) : null}

        {currentResponseType === 'by_fixed_time' ? (
          <FormSelect
            type="select"
            data-testid="escalation-rule-weekday"
            name={EscalationRuleField.WeekDay}
            label="Weekday (optional)"
            options={WEEKDAY_SELECT_OPTIONS}
          />
        ) : null}
      </GenericForm>
    </ModalStep>
  );
};

function toWeekDay(value: string): number | '' {
  return value === NO_WEEKDAY ? '' : Number(value);
}

function getInitialServiceLevels(initialRule?: EscalationRule | null): string[] {
  if (initialRule?.serviceLevels === 'all') {
    return [];
  }

  if (Array.isArray(initialRule?.serviceLevels)) {
    return initialRule.serviceLevels;
  }

  return [];
}

function getInitialWeekDay(initialRule?: EscalationRule | null): string {
  const weekDay = initialRule?.response.weekDay;
  if (initialRule?.response.type !== 'by_fixed_time' || typeof weekDay !== 'number') {
    return NO_WEEKDAY;
  }

  return String(weekDay);
}

function buildDefaultValues(initialRule?: EscalationRule | null): EscalationRuleFormValues {
  const schedule = initialRule?.timeCondition.customSchedule ?? [];
  const weekDays =
    schedule.length > 0
      ? [...new Set(schedule.map((window) => window.weekDay))]
      : DEFAULT_CUSTOM_WEEK_DAYS;

  return {
    [EscalationRuleField.Name]: initialRule?.name ?? '',
    [EscalationRuleField.ServiceLevelSelector]:
      initialRule?.serviceLevels === 'all'
        ? ServiceLevelSelector.All
        : ServiceLevelSelector.Selected,
    [EscalationRuleField.ServiceLevels]: getInitialServiceLevels(initialRule),
    [EscalationRuleField.TimeCondition]: initialRule?.timeCondition.type ?? 'always',
    [EscalationRuleField.ResponseType]: initialRule?.response.type ?? 'fixed_duration',
    [EscalationRuleField.DurationMinutes]: String(initialRule?.response.durationMinutes ?? 60),
    [EscalationRuleField.BusinessDays]: String(initialRule?.response.businessDays ?? 1),
    [EscalationRuleField.FixedTime]: initialRule?.response.time ?? '17:00',
    [EscalationRuleField.WeekDay]: getInitialWeekDay(initialRule),
    [EscalationRuleField.CustomWeekDays]: weekDays.map(String),
    [EscalationRuleField.CustomTimeFrom]: schedule[0]?.timeFrom ?? '09:00',
    [EscalationRuleField.CustomTimeTo]: schedule[0]?.timeTo ?? '17:00',
  };
}

function toEscalationRule(
  values: EscalationRuleFormValues,
  id: string,
  order: number,
): EscalationRule {
  const allServiceLevels = values.serviceLevelSelector === ServiceLevelSelector.All;
  const weekDay = toWeekDay(values.weekDay);

  return {
    id,
    order,
    name: values.name || undefined,
    serviceLevels: allServiceLevels ? 'all' : values.serviceLevels,
    timeCondition: {
      type: values.timeCondition,
      customSchedule:
        values.timeCondition === 'custom'
          ? values.customWeekDays.map((day) => ({
              weekDay: Number(day),
              timeFrom: values.customTimeFrom,
              timeTo: values.customTimeTo,
            }))
          : undefined,
    },
    response: {
      type: values.responseType,
      durationMinutes:
        values.responseType === 'fixed_duration' ? Number(values.durationMinutes) : undefined,
      businessDays:
        values.responseType === 'within_business_days'
          ? Number(values.businessDays)
          : undefined,
      time: TIME_ESCALATION_RESPONSE_TYPES.includes(values.responseType)
        ? values.fixedTime
        : undefined,
      weekDay: values.responseType === 'by_fixed_time' && weekDay !== '' ? weekDay : undefined,
    },
  };
}

interface PreviewParams {
  urgencyLabel: string;
  allServiceLevels: boolean;
  serviceLevels: string[];
  timeCondition: EscalationTimeConditionType;
  responseType: EscalationResponseType;
  durationMinutes: number;
  businessDays: number;
  fixedTime: string;
  weekDay: number | '';
}

function buildRulePreview(params: PreviewParams): string {
  const {
    urgencyLabel,
    allServiceLevels,
    serviceLevels = [],
    timeCondition,
    responseType,
    durationMinutes,
    businessDays,
    fixedTime,
    weekDay,
  } = params;

  let serviceLevelText;

  if (allServiceLevels || serviceLevels.length === 0) {
    serviceLevelText = 'any client';
  } else if (serviceLevels.length === 1) {
    serviceLevelText = `${serviceLevels[0]} clients`;
  } else {
    const allButLast = serviceLevels.slice(0, -1).join(', ');
    const last = serviceLevels[serviceLevels.length - 1];
    serviceLevelText = `${allButLast} or ${last} clients`;
  }

  const timeText = formatEnumLabel(timeCondition);

  let responseText: string;

  switch (responseType) {
    case 'fixed_duration':
      responseText =
        durationMinutes >= 60 && durationMinutes % 60 === 0
          ? `within ${durationMinutes / 60} hour${durationMinutes / 60 !== 1 ? 's' : ''}`
          : `within ${durationMinutes} minutes`;
      break;
    case 'within_business_days':
      responseText = formatWithinBusinessDays(businessDays);
      break;
    case 'by_end_of_day':
      responseText = 'by end of day';
      break;
    case 'by_fixed_time': {
      const weekdayLabel = ESCALATION_WEEKDAY_OPTIONS.find(
        (option) => option.value === weekDay,
      )?.label;
      responseText = weekdayLabel ? `by ${fixedTime} on ${weekdayLabel}` : `by ${fixedTime}`;
      break;
    }
    case 'by_next_business_day':
      responseText = `by ${fixedTime} next business day`;
      break;
    case 'by_tomorrow':
      responseText = `by ${fixedTime} tomorrow`;
      break;
    case 'best_effort':
      responseText = 'as soon as we can (best effort)';
      break;
    default:
      responseText = 'best effort';
  }

  return `If escalated as ${urgencyLabel} by ${serviceLevelText} ${timeText}, respond ${responseText}.`;
}
